import { createSlice } from "@reduxjs/toolkit";
import { FirestoreService } from "../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../api/firebase/references";
import { ToastMessages } from "../../components/toasts/toastMessages";
import { NccnRegimenConfig } from "../../data-model/nccnRegimenConfig";

export interface NccnRegimenConfigSliceModel {
  data: NccnRegimenConfig[];
  loading: boolean;
  error: string | null;
}

const initialState: NccnRegimenConfigSliceModel = {
  data: [],
  loading: false,
  error: null,
};

const nccnRegimenConfigSlice = createSlice({
  name: "nccnRegimenConfig",
  initialState,
  reducers: {
    fetchingNccnRegimenConfig: (state) => {
      state.loading = true;
      state.error = null;
      state.data = [];
    },
    setNccnRegimenConfig: (state, action) => {
      state.data = action.payload;
      state.loading = false;
      state.error = null;
    },
    errorFetchingNccnRegimenConfig: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    resetNccnRegimenConfig: (state) => {
      state.data = [];
      state.loading = false;
      state.error = null;
    },
  },
});

export const {
  fetchingNccnRegimenConfig,
  setNccnRegimenConfig,
  errorFetchingNccnRegimenConfig,
  resetNccnRegimenConfig,
} = nccnRegimenConfigSlice.actions;

export const fetchNccnRegimenConfigFromFirebase = () => async (dispatch) => {
  dispatch(fetchingNccnRegimenConfig());
  try {
    const docs = await FirestoreService.getAllDocuments<NccnRegimenConfig>(
      FirestoreCollectionReference.nccnRegimenConfig(),
    );

    dispatch(setNccnRegimenConfig(docs));
  } catch (error) {
    dispatch(errorFetchingNccnRegimenConfig((error as Error).message));
  }
};

export const createNccnRegimenConfig =
  (data: Omit<NccnRegimenConfig, "id">, usePreviousConfigId?: string) =>
  async (dispatch, getState) => {
    try {
      let configData: Omit<NccnRegimenConfig, "id"> = { ...data };

      // If using previous config, copy its data
      if (usePreviousConfigId) {
        const state = getState();
        const previousConfig = state.nccnRegimenConfig.data.find(
          (config) => config.id === usePreviousConfigId,
        );
        if (!previousConfig) {
          throw new Error(ToastMessages.DX_CODE_CONFIG_PREVIOUS_NOT_FOUND);
        }
        configData = {
          ...configData,
          icdcodes: previousConfig.icdcodes || [],
          priority_config_org_specific:
            previousConfig.priority_config_org_specific,
        };
      }

      // Use regimen_code as document ID
      const newDoc =
        await FirestoreService.addDocumentWithId<NccnRegimenConfig>(
          FirestoreCollectionReference.nccnRegimenConfig(),
          {
            id: data.regimen_code, // Use regimen_code as document ID
            ...configData,
          } as any,
        );

      // Refresh the list
      dispatch(fetchNccnRegimenConfigFromFirebase());
      return newDoc;
    } catch (error) {
      dispatch(errorFetchingNccnRegimenConfig((error as Error).message));
      throw error;
    }
  };

export const updateNccnRegimenConfig =
  (docId: string, updates: Partial<NccnRegimenConfig>) => async (dispatch) => {
    try {
      await FirestoreService.updateDocument(
        FirestoreCollectionReference.nccnRegimenConfig(),
        docId,
        updates,
      );

      // Refresh the list
      dispatch(fetchNccnRegimenConfigFromFirebase());
    } catch (error) {
      dispatch(errorFetchingNccnRegimenConfig((error as Error).message));
      throw error;
    }
  };

export default nccnRegimenConfigSlice.reducer;
